import React from 'react';
import { motion } from 'framer-motion';
import Button from '../common/Button';
import { ArrowRight, Sparkles } from 'lucide-react';

const Hero = () => {
    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">

            {/* Geometric Mesh Background */}
            <div className="absolute inset-0 bg-white">
                {/* Dot grid pattern */}
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: 'radial-gradient(circle, #bebd19 1px, transparent 1px)',
                        backgroundSize: '32px 32px',
                    }}
                />
                {/* Accent glows */}
                <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#bebd19]/8 rounded-full blur-[120px]" />
                <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#bebd19]/5 rounded-full blur-[100px]" />
                {/* Subtle cross-hatch lines */}
                <div
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage: 'linear-gradient(45deg, #000 1px, transparent 1px), linear-gradient(-45deg, #000 1px, transparent 1px)',
                        backgroundSize: '60px 60px',
                    }}
                />
            </div>

            {/* Soft fade at bottom for smooth transition */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white z-0" />

            <div className="container mx-auto px-4 relative z-10 text-center">

                {/* Badge */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#bebd19]/30 bg-[#bebd19]/10 backdrop-blur-sm mb-8"
                >
                    <Sparkles size={16} className="text-[#bebd19]" />
                    <span className="text-sm font-semibold text-[#bebd19]">Premier Digital Marketing Agency in Pune</span>
                </motion.div>

                {/* Main Heading */}
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="text-6xl md:text-8xl font-black text-gray-900 tracking-tight leading-[1.1] mb-8"
                >
                    Build Faster <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#bebd19] via-[#a3a215] to-[#bebd19]">
                        Scale Smarter
                    </span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-xl md:text-2xl text-gray-500 max-w-3xl mx-auto mb-12 leading-relaxed font-light"
                >
                    PyrusMedia is a full-service digital marketing company and IT solutions provider. We engineer high-performance web applications, mobile apps, custom digital solutions, and AI automation to accelerate your business growth.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="flex flex-col sm:flex-row gap-6 justify-center items-center"
                >
                    <Button to="/contact" className="w-full sm:w-auto text-lg px-10 py-4 shadow-[0_0_40px_-10px_rgba(190,189,25,0.4)]">
                        Start Your Project
                    </Button>
                    <Button to="/services" variant="outline" className="w-full sm:w-auto text-lg px-10 py-4 group">
                        Explore Services <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                    </Button>
                </motion.div>

                {/* Stats / Social Proof */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.8 }}
                    className="mt-20 pt-10 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
                >
                    {[
                        { label: 'Happy Clients', value: '20+' },
                        { label: 'Projects Shipped', value: '20+' },
                        { label: 'Years Experience', value: '5+' },
                        { label: 'Team Experts', value: '10+' },
                    ].map((stat, i) => (
                        <div key={i}>
                            <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                            <div className="text-sm text-gray-400 font-medium uppercase tracking-wider">{stat.label}</div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
